import React, { useEffect, useRef, useState } from 'react';
import {
  useGetCompletedTaxReturnsQuery,
  useGetLodgeReturnListQuery,
} from '@/pages/tax/apis/lodgeReturnAPI';
import { formatDate } from '@/utils/commonUtils';
import {
  Card,
  Input,
  Table,
  Button,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Divider,
  App,
  Flex,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import TaxMeeting from '@/pages/tax/components/TaxMeeting';
import ApplicationHeading from '@/components/Elements/Heading/ApplicationHeading';
import CalculatorListComponent from '@/components/Calculator/Calculator';
import { useCreatePaymentCheckoutMutation } from '@/store/apis/paymentApi';
import { displayError } from '@/utils/displayMessageUtils';
import useScrollToTop from '@/hooks/useScrollTop';
import {
  paymentStatus,
  PaymentStatusKey,
  PaymentStatusType,
} from '@/constant/titleMapper';
import { FormOutlined } from '@ant-design/icons';
import ApplicationSubHeading from '@/components/Elements/Heading/ApplicationSubHeading';
import TableTitle from '@/components/Elements/Heading/TableTitle';
import { useFetchUserDataQuery } from '@/store/apis/userApi';
import Meeting from '@/components/Meeting/Meeting';

const LodgeReturn = () => {
  const [meetingUrl, setMeetingUrl] = useState<string>('');
  const { data: lodgeReturnList } = useGetLodgeReturnListQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [createPaymentCheckout, { isLoading: createPaymentCheckoutLoading }] =
    useCreatePaymentCheckoutMutation();

  const [taxReturnsListData, setTaxReturnsListData] = useState<any[]>([]);

  const {
    data: taxReturnsList,
    isLoading: taxReturnsListLoading,
    error: taxReturnsListError,
  } = useGetCompletedTaxReturnsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const hasShownErrorRef = useRef(false);

  useEffect(() => {
    if (taxReturnsListError && !hasShownErrorRef.current) {
      displayError(taxReturnsListError);
      hasShownErrorRef.current = true;
    }
  }, [taxReturnsListError]);

  const { data: userProfileData, refetch } = useFetchUserDataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const userProfile = userProfileData?.data;

  useEffect(() => {
    if (taxReturnsList) {
      setTaxReturnsListData(taxReturnsList.data);
    }
  }, [taxReturnsList]);

  const navigate = useNavigate();
  useScrollToTop();
  const handleCreatePaymentCheckout = (id: any) => {
    createPaymentCheckout({ application_id: id, service_name: 'tax' })
      .unwrap()
      .then((res) => {
        if (res.data.redirect) {
          window.location.href = res.data.redirect;
        }
      })
      .catch((err) => {
        displayError(err);
      });
  };

  const columns: any = [
    // {
    //   title: <TableTitle>S.N</TableTitle>,
    //   key: 'index',
    //   align: 'center',
    //   render: (_: any, __: any, index: number) => (
    //     <Typography.Text style={{ fontWeight: '600' }}>
    //       {index + 1}.
    //     </Typography.Text>
    //   ),
    //   width: 100,
    // },
    {
      title: <TableTitle>ID</TableTitle>,
      dataIndex: 'id',
      key: 'id',
      align: 'center',
      render: (text: string) => <Typography.Text>{text + '.'}</Typography.Text>,
      width: 200,
    },
    // {
    //   title: <TableTitle>Approval Status</TableTitle>,
    //   dataIndex: 'status',
    //   key: 'status',
    //   align: 'center',
    //   render: (_: any, { status }: { status: string }) => {
    //     const color = status === 'Pending' ? 'orange' : 'green';
    //     return <Tag color={color}>{status?.toUpperCase()}</Tag>;
    //   },
    //   width: 200,
    // },
    // {
    //   title: <TableTitle>Tax Year</TableTitle>,
    //   dataIndex: 'tax_year',
    //   key: 'tax_year',
    //   align: 'center',
    //   width: 200,
    // },
    {
      title: <TableTitle>Submission Date</TableTitle>,
      dataIndex: 'created_at',
      key: 'created_at',
      align: 'center',
      render: (_: any, { created_at }: { created_at: string | null }) => {
        if (!created_at) return <Typography.Text>N/A</Typography.Text>;
        return (
          <Typography.Paragraph>{formatDate(created_at)}</Typography.Paragraph>
        );
      },
      width: 250,
    },
    {
      title: <TableTitle>Payment Status</TableTitle>,
      dataIndex: 'payment_status',
      key: 'payment_status',
      align: 'center',
      render: (
        _: any,
        { payment_status }: { payment_status: PaymentStatusKey },
      ) => {
        const color = payment_status === 'PAID' ? 'green' : 'orange';
        return <Tag color={color}>{paymentStatus[payment_status]}</Tag>;
      },
      width: 200,
    },
    {
      title: <TableTitle>Action</TableTitle>,
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      render: (
        _: any,
        {
          id,
          payment_status,
        }: { id: number; payment_status: PaymentStatusKey },
      ) => {
        if (payment_status !== 'PAID') {
          return (
            <Button
              loading={createPaymentCheckoutLoading}
              disabled={createPaymentCheckoutLoading}
              type="primary"
              size="small"
              onClick={() => {
                handleCreatePaymentCheckout(id);
              }}
            >
              Proceed to Pay
            </Button>
          );
        } else {
          return (
            <Button type="default" size="small">
              View Progress
            </Button>
          );
        }
      },
      width: 200,
    },
  ];

  const tax_returns_columns: any = [
    // {
    //   title: <TableTitle>S.N</TableTitle>,
    //   key: 'index',
    //   align: 'center',
    //   render: (_: any, __: any, index: number) => (
    //     <Typography.Text strong>{index + 1}</Typography.Text>
    //   ),
    //   width: 100,
    // },
    {
      title: <TableTitle>Object ID</TableTitle>,
      dataIndex: 'hs_object_id',
      key: 'hs_object_id',
      align: 'center',
      render: (text: string) => (
        <Typography.Text>{text || 'N/A'}</Typography.Text>
      ),
      width: 200,
    },
    {
      title: <TableTitle>Deal Name</TableTitle>,
      dataIndex: 'dealname',
      key: 'dealname',
      align: 'center',
      render: (text: string) => (
        <Typography.Text>{text || 'N/A'}</Typography.Text>
      ),
      width: 200,
    },
    // {
    //   title: <TableTitle>Amount</TableTitle>,
    //   dataIndex: 'amount',
    //   key: 'amount',
    //   align: 'center',
    //   render: (text: string) => (
    //     <Typography.Text>{text || 'N/A'}</Typography.Text>
    //   ),
    //   width: 200,
    // },
    // {
    //   title: <TableTitle>Close Date</TableTitle>,
    //   dataIndex: 'closedate',
    //   key: 'closedate',
    //   align: 'center',
    //   render: (_: any, { closedate }: { closedate: string }) => (
    //     <Typography.Text>{formatDate(closedate)}</Typography.Text>
    //   ),
    //   width: 200,
    // },
    // {
    //   title: <TableTitle>Create Date</TableTitle>,
    //   dataIndex: 'createdate',
    //   key: 'createdate',
    //   align: 'center',
    //   render: (_: any, { createdate }: { createdate: string }) => (
    //     <Typography.Text>{formatDate(createdate)}</Typography.Text>
    //   ),
    //   width: 200,
    // },
    {
      title: <TableTitle>Deal Stage</TableTitle>,
      dataIndex: 'deal_stage_name',
      key: 'deal_stage_name',
      align: 'center',
      render: (_: any, { deal_stage_name }: { deal_stage_name: string }) => {
        const color = deal_stage_name === 'in_progress' ? 'orange' : 'green';
        return <Tag color={color}>{deal_stage_name.toUpperCase()}</Tag>;
      },
      width: 200,
    },
    // {
    //   title: <TableTitle>Pre-approval Expiry Date</TableTitle>,
    //   dataIndex: 'aip_expiry',
    //   key: 'aip_expiry',
    //   align: 'center',
    //   render: (_: any, { aip_expiry }: { aip_expiry: string | null }) =>
    //     aip_expiry ? (
    //       <Typography.Text>{formatDate(aip_expiry)}</Typography.Text>
    //     ) : (
    //       <Typography.Text>N/A</Typography.Text>
    //     ),
    //   width: 200,
    // },
  ];

  const startApplication = () => {
    navigate('/tax/lodge-return/application');
  };

  useEffect(() => {
    if (!userProfile) return;
    const params = new URLSearchParams({
      embed: 'true',
      ...(userProfile?.firstName && { firstname: userProfile?.firstName }),
      ...(userProfile?.lastName && { lastname: userProfile?.lastName }),
      ...(userProfile?.email && { email: userProfile?.email }),
      ...(userProfile?.phone && { phone: userProfile?.phone }),
    });
    setMeetingUrl(
      `https://meetings.hubspot.com/pau-lam/tax-consultation?${params}`,
    );
  }, [userProfile]);

  return (
    <div
      style={{
        width: '100%',
        marginTop: '20px',
      }}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card
            title={
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography.Title level={5} style={{ margin: '0' }}>
                  Tax Return Preparation
                </Typography.Title>
                <Button
                  type="primary"
                  style={{ width: '100%', maxWidth: '150px' }}
                  onClick={startApplication}
                >
                  Prepare Tax Return
                </Button>
              </div>
            }
          >
            <Table
              bordered
              columns={columns}
              dataSource={lodgeReturnList?.data}
              style={{
                width: '100%',
              }}
              scroll={{
                x: 'max-content',
              }}
            />
          </Card>
        </Col>
        <Col xs={24}>
          <Divider />
        </Col>
        <Col xs={24}>
          <Card title="Lodged Tax Returns">
            <Table
              bordered
              style={{
                width: '100%',
              }}
              columns={tax_returns_columns}
              loading={taxReturnsListLoading}
              dataSource={taxReturnsListData}
              scroll={{
                x: 'max-content',
              }}
            />
          </Card>
        </Col>
        <Col xs={24}>
          <Divider />
        </Col>
        <Col xs={24} md={24} lg={12}>
          <Meeting meetingUrl={meetingUrl} title="Book a Meeting" />
        </Col>
        <Col xs={24} md={24} lg={12}>
          <CalculatorListComponent title="Tax Calculators" />
        </Col>
      </Row>
    </div>
  );
};

export default LodgeReturn;
